import { BaseEditDialog } from '@features/user-account/components/BaseEditDialog';
import { StyledIconButton } from '@features/user-account/components/StyledIconButton';
import { useAdminApi } from '@hooks/useAdminApi';
import { Box, Stack, Typography } from '@mui/material';
import { ORGANIZATION_TYPE_OPTIONS, Organization, OrganizationTypeEnum } from '@protos/organization';
import { TextInputSelectBox } from '@shared/components/TextFieldSelectBox';
import { useEffect, useState } from 'react';

interface EditOrganizationTypesDialogProps {
  organization: Organization;
  isOpen: boolean;
  onClose: () => void;
  onOrganizationUpdated: (organization: Organization) => void;
}

export const EditOrganizationTypesDialog: React.FC<EditOrganizationTypesDialogProps> = ({ organization, isOpen, onClose, onOrganizationUpdated }) => {
  const { apiClient } = useAdminApi();
  const [types, setTypes] = useState<OrganizationTypeEnum[]>([...(organization.types ?? [])]);

  const onSave = async () => {
    if (apiClient) {
      const updated = await apiClient.updateOrganization(organization.id, { types });
      onOrganizationUpdated(updated);
    }
  };

  const onTypeAdd = () => {
    const available = ORGANIZATION_TYPE_OPTIONS.find(t => !types.includes(t));
    if (available) {
      setTypes([...types, available]);
    }
  };

  const onTypeRemove = (id: number) => {
    const updated = [...types];
    updated.splice(id, 1);
    setTypes(updated);
  };

  const onTypeUpdate = (id: number, value: OrganizationTypeEnum) => {
    const updated = [...types];
    updated[id] = value;
    setTypes(updated);
  };

  useEffect(() => {
    setTypes(organization.types ?? []);
  }, [organization, isOpen]);

  return (
    <BaseEditDialog
      title="Edit Organization Types"
      isOpen={isOpen}
      onClose={onClose}
      clearErrorOn={[types]}
      defaultErrorMessage="There was an error updating the organization types"
      onSave={onSave}
    >
      <Box display="flex" flexDirection="column" marginRight={2} gap={1}>
        <Typography fontSize={11}>
          AGENT is required before AGENT_ADMIN can be granted to this organization&apos;s users, or before it can be the agent side of a link.
          Removing it revokes that authority immediately. PRINCIPAL is a label and grants nothing.
        </Typography>
        <Stack direction="column" alignItems="flex-start">
          {types.map((type, id) => (
            <Stack key={`organization_type_${type}_${id}`} direction="row">
              <TextInputSelectBox
                label="Type"
                value={type}
                sx={{ width: '180px' }}
                onChange={(value: OrganizationTypeEnum) => onTypeUpdate(id, value)}
                options={ORGANIZATION_TYPE_OPTIONS.filter(t => !types.includes(t) || t === type)}
              />
              <StyledIconButton onClick={() => onTypeRemove(id)} size={'small'} style={{ marginLeft: '5px' }}>
                <i className={'ri-delete-bin-line'} />
              </StyledIconButton>
            </Stack>
          ))}
          <StyledIconButton onClick={onTypeAdd} disabled={types.length >= ORGANIZATION_TYPE_OPTIONS.length}>
            <i className={'ri-add-line'} />
          </StyledIconButton>
        </Stack>
      </Box>
    </BaseEditDialog>
  );
};
